<style></style>

<template>
    <div class="page-container">
        <h1>Orientation</h1>

        <section-collapse accordion="orientation">
            <template #title>Why it's Important</template>
            <template #collapsed-content>
                <p>Orientation is a critical step because it is the child care providers’ first hands-on experience with the Go NAPSACC tools. In-person time sitting with child care providers is essential as they are building their skills and comfort using these tools. You are also building relationships. Creating a trusting relationship in the beginning makes a smoother journey toward healthier practices. Child care providers are more comfortable reaching out for help when you have mutual trust.</p>
            </template>
        </section-collapse>

        <section-collapse id="orientation-activities" accordion="orientation">
            <template #title>Activities</template>
            <template #collapsed-content>
                <activity-collapse>
                    <template #number>1</template>
                    <template #title>
                        <strong>Consultant</strong> orients child care providers to Go NAPSACC
                    </template>
                    <template #collapsed-content>
                        <div>
                            <p>The goals of orientation are to introduce Go NAPSACC, explain its 5-step improvement process, and build comfort with its online tools. <a :href="$basePath + '/uploads/orientation_slideshow.pptx'" target="_blank" class="text-warning"><strong>Slides and talking points</strong></a> have been created to help you with this orientation session. By the end of orientation, child care providers should feel knowledgeable and excited about Go NAPSACC and be ready to start the improvement process.</p>
                        </div>

                        <h4>Orientation Prep:</h4>
                        <div>
                            <p>Ensure that child care providers designated as key contacts have a Go NAPSACC account.</p>
                        </div>
                        <ul class="list-instructions">
                            <li>
                                Use your Consultant Tools to send invitations to register for an account.
                            </li>
                            <li>
                                Monitor completed registrations. If a child care provider is not registered 3 days prior to orientation, send a reminder and offer to  troubleshoot challenges.
                            </li>
                        </ul>

                        <div>
                            <p>Refresh your familiarity with Go NAPSACC and the Provider Tools.</p>
                        </div>
                        <ul class="list-instructions">
                            <!-- UPDATE THE DEMO LINK BELOW -->
                             <!-- !!!!!!!!!!!!!!!!!! -->
                            <li>Make sure that you can access the <a href="https://napsacc.schsr.unc.edu/napsaccdemo/">Go NAPSACC DEMO</a> website and your demo child care provider account. You created this account as part of a homework assignment when you received your Go NAPSACC training. You will use this demo website and account during orientation.</li>
                            <!-- !!!!!!!!!!!!!!!!!! -->
                            <li>Practice delivering the <a :href="$basePath + '/uploads/orientation_slideshow.pptx'" target="_blank" class="text-warning"><strong>Orientation slideshow</strong></a>.</li>
                        </ul>

                        <p>Schedule orientation sessions with child care providers. These can be conducted one-on-one or in small groups.</p>
                        <ul class="list-instructions">
                            <li>For one-on-one sessions, arrange to meet the child care provider at their facility and to use one of their computers. Confirm internet access.</li>
                            <li>For small group sessions, invite 3-5 child care providers to one location. You may want to use your office, a local library, or a community college. Wherever you meet, make sure that you have a quiet space with easy access to computers and internet.</li>
                            <li>Evening and weekend sessions may be needed to accommodate their schedules.</li>
                            <li>Allow about 1 hour for these sessions.</li>
                            <li>Send a reminder a couple of days prior to the session. </li>
                            <li>Make sure that you have all necessary resources (e.g., projector for small group trainings, reliable internet, laptop or desktop computer).</li>
                            <li>Print out the <a :href="$basePath + '/uploads/timeline_6months.pdf'" target="_blank" class="text-warning"><strong>6-month</strong></a> or <a :href="$basePath + '/uploads/timeline_12months.pdf'" target="_blank" class="text-warning"><strong>12-month</strong></a> timeline.</li>
                        </ul>

                        <h4>Orientation Agenda:</h4>
                        <p>The <a :href="$basePath + '/uploads/orientation_slideshow.pptx'" target="_blank" class="text-warning"><strong>Orientation slideshow</strong></a> guides you through the following agenda.</p>
                        <ul class="list-instructions">
                            <li><strong>Introduce Go NAPSACC.</strong> Review its purpose, history, modules, and 5-step improvement process.</li>
                            <li><strong>Build comfort with online tools.</strong> Provide a demonstration and hands on learning in the Go NAPSACC website. During this time, you can offer help if child care providers experience any difficulties.</li>
                            <li><strong>Review expectations.</strong> Use a timeline to outline to child care providers what they will be doing over the coming months. Be sure to call attention to the next step (self-assessment) and the deadline for completing it. </li>
                            <li><strong>Offer your support.</strong> Reassure child care providers that you are there to give them support and help hold themselves accountable. Let them know that you will be checking in with them regularly/monthly by email, phone, or in-person visits to discuss progress and to address any challenges they encounter. It can be helpful to go ahead and set a date for your first check-in.</li>
                        </ul>

                        <helpful-hints>
                            <h5>Invite all key contacts to orientation</h5>
                            <p>Key contacts may vary between different types of child care programs. For family child care homes, the key contact is likely the owner and main child care provider. For centers, the key contact should ideally be the director or another administrator. If desired, other administrators and/or teachers can attend as well.</p>

                        </helpful-hints>

                    </template>
                </activity-collapse>
            </template>
        </section-collapse>

        <section-collapse id="orientation-common-challenges" accordion="orientation">
            <template #title>Common Challenges &amp; Solutions</template>
            <template #collapsed-content>
                <h4 class="mt-0">Website looks different or strange</h4>
                <p>
                    If the child care provider is working from a laptop or desktop computer, check the Internet browser. Go NAPSACC is designed to work in Chrome, Edge, Firefox, Internet Explorer (IE11 or later), and Safari. If they are working on a mobile phone, be mindful of the page they are viewing. The homepage, registration, and self-assessments are easily viewed on a mobile phone. The action planning tool and tips &amp; materials library may not be as easy to navigate.
                </p>

                <h4>Unreliable internet access</h4>
                <p>
                    You may want to bring your own laptop or tablet, along with a hot spot, to the orientation session. Or, you may want to conduct the orientation at a local library, where you can access a computer and the internet.
                </p>

                <h4>Child care provider runs multiple programs</h4>
                <p>
                    They need to register for two separate accounts with two separate email addresses, one for each child care program. If each child care program has a general email account, such as info@abcchildcare.com, they could opt to use those email addresses. Both email addresses need to be associated with working accounts that they can access in case they ever need to reset their password.
                </p>

                <h4>Desire for teachers and other staff to have Go NAPSACC accounts</h4>
                <p>
                    The website does allow teachers and other staff to register for additional accounts and these accounts will be connected. When they register and they begin entering the child care program name, the website will try to fill in the remaining information. They need to make sure that all information, like the child care program name and location, is correct so that their accounts can be linked to the primary account for their child care program. 
                </p>

                <p>
                    Make sure to understand why they want teachers and other staff to have their own accounts. These accounts can be helpful in giving teachers and other staff direct access to tips &amp; materials and trainings. However, it is important that they not take any new self-assessment, set any new goals, or create any new action plans, because this can disrupt the ability to track child care program-level activities.
                </p> 

                <h4>Connecting with child care providers already registered</h4>
                <p>   
                    In your Consultant Tools, go to your Connections page. The list at the top of the page shows registered child care programs and allows you to invite them to connect. You will be connected once they accept your invitation.  
                </p>    

                <h4>Taking over for another Consultant</h4>
                <p>
                    You will need to connect with these child care providers through your account. In your Consultant Tools, go to your Connections page. The list at the top of the page will show registered child care program and allow you to invite them to connect. Make sure you reach out to these child care providers to let them know you are replacing their previous Consultant and will be reviewing their progress to date. Remind them to accept you as a connection on the Go NAPSACC site so you can see their information.
                </p>
                <p>
                    If the prior Consultant used the Add TA Activity feature, you can view past notes from their work with child care providers. This can help with continuity of service.
                </p> 

                <h4>My Programs dashboard is missing connected child care programs</h4>
                <p>
                    When you invite a registered child care provider to connect, they need to accept your connection from their account. They should have received an email about this request and there should be a message at the top of their screen when they log in. In case they have not seen either, you may want to email, call, or meet with them in person to make sure they saw your invitation and accept the connection.
                </p>
            </template>
        </section-collapse>

        <section-collapse id="orientation-support-documents" accordion="orientation">
            <template #title>Support Documents</template>
            <template #collapsed-content>
                <slot name="support-docs"><div>some backup text for support docs in orientation</div></slot>
            </template>
        </section-collapse>

        <video-modal :video="orientationVideo" v-model="showModal"></video-modal>


    </div>
</template>

<script>
    import VideoModal from '../../materials/VideoModal'
    import SectionCollapse from '../../TaResources/SectionCollapse.vue';
    import HelpfulHints from '../../TaResources/HelpfulHints.vue';
    import ActivityCollapse from '../../TaResources/ActivityCollapse.vue';
    import ConsultantToolsSpotlight from '../../TaResources/ConsultantToolsSpotlight.vue';

    export default {
        components: {
            VideoModal,
            SectionCollapse,
            HelpfulHints,
            ActivityCollapse,
            ConsultantToolsSpotlight,            
        },
        data() {
            return {
                showModal: false
            }
        },
        methods: {},
        computed: {
            orientationVideo: () => {
                return {
                    url: 'https://www.youtube.com/watch?v=7zIyqBowkfw',
                    title: 'Orientation video'
                }
            }
        }
    }
</script>